import { XMLParser } from "fast-xml-parser";
import { AxiosRequestConfig } from "axios";
import { CoObjectItem } from "../../components/interfaces/CoObject";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { XmlDocumentMetadata } from "../../api/models/xml-document-metadata";
import { DocumentData } from "../../components/document/document-types";
import { DimensionParameter } from "../../api/models/dimension-parameter";
import { DimensionHierarchy } from "../../api/models/dimension-hierarchy";
import { fetchXmlMetadata } from "../../api/document/DocumentsPart";
import { FormatMetadata } from "../../api/models/format-metadata";
import { FormatImage } from "../../api/models/format-image";
import { TemplateInfo } from "../../api/models/template-info";
import { getLogicId } from "../../api/object/CoObjectGeneral";

import { DEFAULT_IMAGE_EXTENSION, DEFAULT_IMAGE_RESOLUTION_MAP } from "../../components/coobjects/ImageDropdown";
import { restoreDefaultDocumentMetadata } from "./DocumentPartMetadataHelper";
import { store } from "../../redux/store/store";
import { setIsLoading, setProperties, updateProperty } from "../../redux/feature/docProperties/doc-properties-slice";

export const CO_OBJECT_NAMESPACE = "CustomXMLCo";
export const CUSTOM_XML_METADATA_NAMESPACE = "CustomXMLCoMetadata";

export enum DocPropKeys {
  TenantId = "Tenant-ID",
  DocumentId = "Document-ID",
  IsInDesignEnabled = "Is-InDesign-Enabled",
  IsRagIndexingEnabled = "Is-Rag-Indexing-Enabled",
}

export const mapDocumentDataToDocumentPartMetadata = (documentData: DocumentData): DocumentPartMetadata => {
  return {
    logicId: documentData.advanced.logicId,
    parametersMetadata: documentData.parameters,
    inheritableParametersMetadata: documentData.inheritableParameters,
    documentId: documentData.documentId,
    templateInfo: documentData.advanced.templateInfo,
    formatMetadata: documentData.advanced.formatMetadata,
    language: documentData.language,
    formatImage: documentData.advanced.formatImage,
    inDesignLink: documentData.advanced.inDesignLink,
  };
};

const toArray = <T>(maybeArray: T | T[]): T[] => {
  if (!maybeArray) return [];
  return Array.isArray(maybeArray) ? maybeArray : [maybeArray];
};

const option = { ignoreAttributes: false, numberParseOptions: { leadingZeros: false, hex: true } };

/**
 * Optional `cfg` (e.g. `{ params: { notShowLoading: true } }`), threaded through to the one
 * network call reachable from this function (`fetchXmlMetadata`, via `normalizeMetadata`/
 * `fetchAndTransformLegacyMetadata`, on the legacy-document fallback branch only). Every other step
 * here is Office.js, never axios. Optional and additive — every existing caller omits it and keeps
 * today's behavior unchanged; only the streaming `runRulesStreaming` path passes it.
 */
export const getDocumentPartMetadataFromCustomXml = (cfg?: AxiosRequestConfig): Promise<DocumentPartMetadata> => {
  return new Promise((resolve) => {
    try {
      Office.context.document.customXmlParts.getByNamespaceAsync(CUSTOM_XML_METADATA_NAMESPACE, function (result) {
        const xmlPart = result.value[0];
        if (xmlPart) {
          console.log(`An xmlPart for ${CUSTOM_XML_METADATA_NAMESPACE} has been found`);
          xmlPart.getXmlAsync({}, async function (eventArgs) {
            const xmlMetadata = new XMLParser(option).parse(eventArgs.value)[CO_OBJECT_NAMESPACE];
            const documentMetadata = await normalizeMetadata(xmlMetadata, cfg);
            resolve(documentMetadata);
          });
        } else resolve(null);
      });
    } catch (e) {
      console.error(e);
      resolve(null);
    }
  });
};

const normalizeMetadata = async (xmlMetadata: any, cfg?: AxiosRequestConfig): Promise<DocumentPartMetadata> => {
  const documentId = await retrieveDocumentProp(DocPropKeys.DocumentId);
  let documentMetadata: DocumentPartMetadata;

  if (xmlMetadata?.version && documentId === xmlMetadata.documentMetadata.documentId) {
    console.log(`CustomXml with version has been found`);
    documentMetadata = xmlMetadata.documentMetadata;
  } else {
    console.log("Legacy customXml has been found, we will fetch the metadata from the database");
    documentMetadata = await fetchAndTransformLegacyMetadata(documentId, cfg);
  }

  if ((documentMetadata.formatImage as any) == "") documentMetadata.formatImage = null;

  return {
    ...documentMetadata,
    parametersMetadata: normalizeParametersMetadata(documentMetadata.parametersMetadata),
    inheritableParametersMetadata: normalizeParametersMetadata(documentMetadata.inheritableParametersMetadata),
    templateInfo: normalizeObject(documentMetadata.templateInfo),
    inDesignLink: normalizeObject(documentMetadata.inDesignLink),
  };
};

const fetchAndTransformLegacyMetadata = async (
  documentId: string,
  cfg?: AxiosRequestConfig
): Promise<DocumentPartMetadata> => {
  try {
    const xmlDocumentMetadata: XmlDocumentMetadata = await fetchXmlMetadata(documentId, cfg);
    const parsedXml = new XMLParser(option).parse(xmlDocumentMetadata?.xmlMetadata);
    const xml = parsedXml[CO_OBJECT_NAMESPACE];
    const documentMetadata: DocumentPartMetadata | undefined = xml?.documentMetadata;

    if (!documentMetadata) {
      return restoreDefaultDocumentMetadata(documentId);
    }

    await replaceCustomXmlMetadata(xmlDocumentMetadata);

    return documentMetadata;
  } catch (e) {
    console.error(e);
    return restoreDefaultDocumentMetadata(documentId);
  }
};

const normalizeObject = (xmlObject: any): any => {
  return xmlObject !== "" ? xmlObject : null;
};

const normalizeParametersMetadata = (xmlParametersMetadata: any): Array<DimensionParameter> => {
  if (!xmlParametersMetadata) {
    console.log("No parametersMetadata has been retrieved");
    return [];
  }

  return toArray(xmlParametersMetadata).map((parameter) => ({
    ...parameter,
    dimensions: toArray(parameter.dimensions).map((dimension) => {
      const { hierarchy, ...others } = dimension;

      return {
        ...others,
        ...(hierarchy !== "" ? { hierarchy } : {}),
        values: toArray(dimension.values).map((value) => ({
          ...value,
          oidPath: toArray(value.oidPath),
        })),
      };
    }),
  }));
};

export function getCoObjectFromCustomXml(xml: string): CoObjectItem | null {
  // this method should be used only with getCoObjectFromTagOrCustomXmlByContentControl
  const xmlObject = new XMLParser(option).parse(xml)[CO_OBJECT_NAMESPACE];
  if (!xmlObject?.contentControlId) return null;

  normalizeComponentReference(xmlObject);
  normalizeDimensions(xmlObject);
  normalizeParameters(xmlObject);
  normalizeFormatImage(xmlObject);
  mapAdvancedMetadata(xmlObject);
  return xmlObject;
}

/**
 * Structural types for the raw CustomXml CoObject fragments the `normalize*` helpers below read and
 * mutate in place, as `fast-xml-parser` produces them. A precise 1:1 type against the fully
 * normalized contracts isn't achievable: fast-xml-parser has no schema, so an absent element parses
 * as `undefined` while a present-but-empty one parses as `""`, and a repeatable element parses as a
 * bare object instead of an array until `toArray()` normalizes it. These types capture exactly the
 * shapes actually read/written below.
 */
interface RawXmlDimensionValue {
  ref?: string;
  oidPath?: string | string[];
  [key: string]: unknown;
}

interface RawXmlDimension {
  values?: RawXmlDimensionValue | RawXmlDimensionValue[];
  hierarchy?: string | DimensionHierarchy;
  [key: string]: unknown;
}

/**
 * Legacy pre-normalization field names and the current `ComponentReference` names
 * `normalizeComponentReference` remaps them to — declared on one flat shape so it can read the
 * legacy names and write the current ones back onto the same property without a type mismatch.
 */
interface RawXmlComponentReference {
  componentLogicId?: string;
  componentDocumentName?: string;
  componentName?: string;
  logicId?: string;
  documentName?: string;
  objectName?: string;
  type?: string;
}

interface RawXmlSource {
  dimensions?: "" | RawXmlDimension | RawXmlDimension[];
  inheritFormatImage?: boolean;
  formatImage?: "" | null | Partial<FormatImage>;
  imageExtension?: string;
  imageResolution?: number;
  [key: string]: unknown;
}

interface RawXmlAdvanced {
  choice?: string;
  locale?: string;
  numberFormat?: string;
  typeNumber?: string;
  percentageFormat?: string;
  typePercentage?: string;
  dateFormat?: string;
  typeDate?: string;
  imageExtension?: string;
  imageResolution?: number;
  templateInfo?: { templateId?: string; templateName?: string };
  inheritFormat?: boolean;
  language?: string;
  formatMetadata?: FormatMetadata;
  formatImage?: FormatImage;
}

const normalizeComponentReference = (xmlObject: {
  info?: { objectType?: string; componentReference?: RawXmlComponentReference; [key: string]: unknown };
}) => {
  if (!xmlObject?.info?.componentReference) {
    return;
  }

  const { componentLogicId, componentDocumentName, componentName } = xmlObject.info.componentReference;
  const { objectType } = xmlObject.info;

  // legacy object, documentId and contentControlId are missing
  xmlObject.info.componentReference = {
    logicId: componentLogicId,
    documentName: componentDocumentName,
    objectName: componentName,
    type: objectType,
  };
};

const normalizeDimensions = (xmlObject: { source?: "" | null | RawXmlSource }) => {
  if (xmlObject.source == undefined) {
    return;
  }

  if (xmlObject.source === "") {
    xmlObject.source = null;
    return;
  }

  if (xmlObject.source.dimensions == undefined) {
    return;
  }

  if (xmlObject.source.dimensions === "") {
    xmlObject.source.dimensions = undefined;
    return;
  }

  xmlObject.source.dimensions = toArray(xmlObject.source?.dimensions).map((dimension) => {
    let reference = null;

    const values = toArray(dimension.values).map(({ ref, ...value }) => {
      if (reference === null && ref !== undefined && ref !== "") {
        reference = ref;
      }
      return {
        ...value,
        oidPath: toArray(value.oidPath),
      };
    });

    return {
      ...dimension,
      reference,
      values,
      hierarchy: dimension.hierarchy === "" ? null : dimension.hierarchy,
    };
  });

  xmlObject.source.dimensions = toArray(xmlObject.source?.dimensions).map((dimension) => ({
    ...dimension,
    values: toArray(dimension.values).map((value) => ({
      ...value,
      oidPath: toArray(value.oidPath),
      ref: value.ref === "" ? undefined : value.ref,
    })),
    hierarchy: dimension.hierarchy === "" ? null : dimension.hierarchy,
  }));
};

const normalizeParameters = (xmlObject: { parameters?: "" | RawXmlDimension | RawXmlDimension[] }) => {
  if (xmlObject.parameters === undefined) {
    return;
  }

  if (xmlObject.parameters === "") {
    xmlObject.parameters = undefined;
    return;
  }

  xmlObject.parameters = toArray(xmlObject.parameters).map((parameter) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values: RawXmlDimensionValue[] = toArray(parameter.values).map(({ ref, ...value }) => ({
      ...value,
      oidPath: toArray(value.oidPath),
    }));

    // pick the first non-undefined ref from values
    const reference = values.find((v) => v.ref !== undefined && v.ref !== "")?.ref ?? null;

    return {
      ...parameter,
      reference,
      values,
      hierarchy: parameter.hierarchy === "" ? null : parameter.hierarchy,
    };
  });
};

// `source` is never `""` here: `normalizeDimensions` (which always runs first) already converts
// an empty-string `source` to `null`. `null`/`undefined` are still handled explicitly below.
const normalizeFormatImage = (xmlObject: { source?: null | RawXmlSource }) => {
  if (
    xmlObject.source === undefined ||
    xmlObject.source === null ||
    xmlObject.source.inheritFormatImage === true ||
    (xmlObject.source.formatImage !== undefined &&
      xmlObject.source.formatImage !== null &&
      xmlObject.source.formatImage !== "" &&
      "imageExtension" in xmlObject.source.formatImage &&
      "imageResolution" in xmlObject.source.formatImage)
  ) {
    return;
  }

  const { imageExtension, imageResolution, ...others } = xmlObject.source;

  xmlObject.source = {
    ...others,
    formatImage: {
      imageExtension: imageExtension ?? DEFAULT_IMAGE_EXTENSION,
      imageResolution: imageResolution ?? DEFAULT_IMAGE_RESOLUTION_MAP.get(imageExtension ?? DEFAULT_IMAGE_EXTENSION),
    },
  };
};

const mapAdvancedMetadata = (xmlObject: { advanced?: RawXmlAdvanced }) => {
  if (xmlObject.advanced) {
    let formatMetadata: FormatMetadata = {};
    let formatImage: FormatImage = {};
    let templateInfo: TemplateInfo = {};

    if (xmlObject.advanced.choice === "DOCUMENT_ROOT") {
      formatMetadata = {
        locale: xmlObject.advanced.locale,
        numberFormatValue: xmlObject.advanced.numberFormat,
        numberType: xmlObject.advanced.typeNumber,
        percentageFormatValue: xmlObject.advanced.percentageFormat,
        percentageType: xmlObject.advanced.typePercentage,
        dateFormatValue: xmlObject.advanced.dateFormat,
        dateType: xmlObject.advanced.typeDate,
      };
      formatImage = {
        imageExtension: xmlObject.advanced.imageExtension,
        imageResolution: xmlObject.advanced.imageResolution,
      };

      if (xmlObject.advanced.templateInfo) {
        templateInfo = {
          templateId: xmlObject.advanced.templateInfo?.templateId,
          templateName: xmlObject.advanced.templateInfo?.templateName,
        };
      }
    }

    xmlObject.advanced = {
      choice: xmlObject.advanced.choice,
      inheritFormat: xmlObject.advanced.inheritFormat,
      formatMetadata,
      formatImage,
      language: xmlObject.advanced.language,
      templateInfo,
    };
  }
};

export const getCustomXmlByContentControlId = async (contentControlId: number): Promise<CoObjectItem | null> => {
  const namespace = CO_OBJECT_NAMESPACE + contentControlId;
  return await getCustomXmlByNamespaceId(namespace);
};

export const getCustomXmlByNamespaceId = async (namespace: string): Promise<CoObjectItem | null> => {
  try {
    const getXmlParts = (): Promise<Office.CustomXmlPart[]> =>
      new Promise((resolve, reject) => {
        Office.context.document.customXmlParts.getByNamespaceAsync(namespace, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(new Error(`Failed to get custom XML parts for namespace: ${namespace}`));
          }
        });
      });

    const getXml = (xmlPart: Office.CustomXmlPart): Promise<string> =>
      new Promise((resolve, reject) => {
        xmlPart.getXmlAsync({}, (eventArgs) => {
          if (eventArgs.status === Office.AsyncResultStatus.Succeeded) {
            resolve(eventArgs.value);
          } else {
            reject(new Error(`Failed to get XML content for namespace: ${namespace}`));
          }
        });
      });

    const xmlParts = await getXmlParts();
    if (!xmlParts.length) {
      console.log(`No custom XML has been found for namespace: ${namespace}`);
      return null;
    }

    const xmlString = await getXml(xmlParts[0]);
    let coObjectFromCustomXml = getCoObjectFromCustomXml(xmlString);
    // we don't wait for the logicId to complete the rendering
    getLogicId(coObjectFromCustomXml.documentId, coObjectFromCustomXml.contentControlId).then((id) => {
      coObjectFromCustomXml.id = id;
    });
    return coObjectFromCustomXml;
  } catch (err) {
    console.error(`Error retrieving custom XML for namespace: ${namespace}`, err);
    return null;
  }
};

/**
 * Ritorna il valore in cache per la chiave specificata, oppure undefined se non presente.
 */
const getCachedProperty = (key: string): string | undefined => {
  const properties = store.getState().docProperties.properties;
  return properties !== null && key in properties ? properties[key] : undefined;
};

/**
 * Legge tutte le custom properties da un contesto Word già aperto, aggiorna il Redux cache
 * e ritorna la mappa completa.
 */
const loadAllPropertiesFromContext = async (context: Word.RequestContext): Promise<Record<string, string>> => {
  console.debug("[DocProps] Loading all properties from Office.js context...");
  const customProps = context.document.properties.customProperties;
  customProps.load("items/key,items/value");
  await context.sync();
  const map: Record<string, string> = {};
  for (const prop of customProps.items) {
    map[prop.key] = prop.value;
  }
  console.debug(`[DocProps] Loaded ${Object.keys(map).length} properties into cache:`, Object.keys(map));
  store.dispatch(setProperties(map));
  return map;
};

/**
 * Carica tutte le custom properties dal file nel Redux cache.
 * Se il cache è già popolato, lo ritorna direttamente senza round-trip Office.js.
 * Gestisce chiamate concorrenti con una promise condivisa.
 */
let _loadingPromise: Promise<Record<string, string>> | null = null;

const loadDocumentPropertiesIntoCache = async (): Promise<Record<string, string>> => {
  const { properties, isLoading } = store.getState().docProperties;
  if (properties !== null) {
    console.debug("[DocProps] Cache already populated, skipping Office.js load.");
    return properties;
  }
  if (_loadingPromise === null) {
    console.debug("[DocProps] Cache miss — starting Office.js bulk load...");
    if (!isLoading) store.dispatch(setIsLoading(true));
    _loadingPromise = (async () => {
      try {
        return await Word.run(loadAllPropertiesFromContext);
      } catch (e) {
        store.dispatch(setIsLoading(false));
        throw e;
      } finally {
        _loadingPromise = null;
      }
    })();
  } else {
    console.debug("[DocProps] Cache miss — joining existing in-flight load...");
  }
  return _loadingPromise;
};

/**
 * Retrieve the specified property from the Word custom properties reusing the context.
 * Controlla prima il Redux cache: se presente, ritorna senza round-trip Office.js.
 * Se assente, carica tutte le properties dal contesto corrente e aggiorna il cache.
 */
export const retrieveDocumentPropFromContext = async (context: Word.RequestContext, key: string) => {
  const cached = getCachedProperty(key);
  if (cached !== undefined) {
    console.debug(`[DocProps] Cache hit (fromContext) for "${key}": "${cached}"`);
    return cached;
  }
  console.debug(`[DocProps] Cache miss (fromContext) for "${key}" — loading all properties...`);
  const map = await loadAllPropertiesFromContext(context);
  return map[key];
};

/**
 * Retrieve the specified property from the Word custom properties.
 * Usa il Redux cache: se non ancora popolato, carica tutte le properties in un solo round-trip.
 */
export const retrieveDocumentProp = async (key: string): Promise<string> => {
  try {
    const cached = getCachedProperty(key);
    if (cached !== undefined) {
      console.debug(`[DocProps] Cache hit for "${key}": "${cached}"`);
      return cached;
    }
    console.debug(`[DocProps] Cache miss for "${key}" — loading all properties...`);
    const properties = await loadDocumentPropertiesIntoCache();
    return properties[key];
  } catch (e) {
    console.error("Error retrieving document property:", e);
  }
};

export const setDocumentProp = async (key: string, value: string) => {
  try {
    console.debug(`Setting document property: ${key}`);
    return await Word.run(async (context) => {
      return await setDocumentPropWithContext(context, key, value);
    });
  } catch (e) {
    console.error("Error setting document property:", e);
  }
};

export const setDocumentPropWithContext = async (context: Word.RequestContext, key: string, value: string) => {
  console.debug(`[DocProps] Writing "${key}" = "${value}" to customXml and updating cache`);
  context.document.properties.customProperties.add(key, value);
  await context.sync();
  store.dispatch(updateProperty({ key, value }));
};

export function deleteXMLPart(contentControlId: number): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const key = CO_OBJECT_NAMESPACE + contentControlId;
      console.log("Delete CustomXml for ccId " + contentControlId);
      deleteXmlPartByNameSpace(key);
      resolve();
    } catch (e) {
      console.error("deleteXMLPart error:", e);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

export function deleteXmlPartByNameSpace(xmlNamespace: string) {
  Office.context.document.customXmlParts.getByNamespaceAsync(xmlNamespace, function (result) {
    if (result.value.length != 0) {
      result.value.forEach((xmlPart) =>
        xmlPart.deleteAsync(function (eventArgs) {
          console.log("The XML Part has been deleted.", xmlNamespace, eventArgs.value);
        })
      );
    }
  });
}

export const replaceCustomXmlMetadata = async (xmlDocumentMetadata: XmlDocumentMetadata) => {
  await Word.run(async (context) => {
    const customXmlParts: Word.CustomXmlPartScopedCollection =
      context.document.customXmlParts.getByNamespace(CUSTOM_XML_METADATA_NAMESPACE);

    customXmlParts.load("items");
    await context.sync();

    const xmlParts = customXmlParts.items;
    console.log(`${xmlParts.length} custom XML parts found`);
    if (xmlParts.length === 0) {
      context.document.customXmlParts.add(xmlDocumentMetadata.xmlMetadata);
      console.log("Added custom XML part");
    } else if (xmlParts.length === 1) {
      xmlParts[0].setXml(xmlDocumentMetadata.xmlMetadata);
      console.log("Replaced custom XML part");
    } else {
      const [first, ...others] = xmlParts;
      first.setXml(xmlDocumentMetadata.xmlMetadata);
      console.log("Replaced custom XML part");
      others.forEach((element) => element.delete());
      console.log("Deleted other custom XML parts");
    }
    await context.sync();
  });
};

/**
 * Extracts CDATA content from XML string
 * @param xml - The XML string containing CDATA
 * @returns The content inside CDATA section or empty string if not found
 */
export const extractDataFromXml = (xml: string): string => {
  if (!xml) return "";
  const start = xml.indexOf("<![CDATA[");
  const end = xml.indexOf("]]>", start + 9);
  return start >= 0 && end > start ? xml.substring(start + 9, end).trim() : "";
};

/**
 * Reads XML content from CustomXmlParts by namespace
 * @param namespace - The namespace to search for
 * @returns The XML string from the first matching part, or empty string if none found
 */
export const getXmlFromNamespace = async (namespace: string): Promise<string> => {
  return await Word.run(async (context) => {
    const parts = context.document.customXmlParts.getByNamespace(namespace);
    parts.load("items");
    await context.sync();
    const items = parts.items;
    if (!items || items.length === 0) return "";

    const first = items[0];
    const xmlResult = first.getXml();
    await context.sync();
    // eslint-disable-next-line office-addins/load-object-before-read
    return xmlResult.value || "";
  });
};

/**
 * Reads and parses data from CustomXml using provided parser function
 * @param namespace - The namespace to search for
 * @param parser - Function to parse XML string to desired type
 * @param logPrefix - Prefix for error logging
 * @returns Parsed data or empty array on error
 */
export const getDataFromCustomXml = async <T>(
  namespace: string,
  parser: (xml: string) => T[],
  logPrefix: string
): Promise<T[]> => {
  try {
    const xml = await getXmlFromNamespace(namespace);
    return parser(xml);
  } catch (e) {
    console.error(`${logPrefix} Unexpected error:`, e);
    return [];
  }
};

/**
 * Persists data to CustomXml as JSON wrapped in CDATA
 * @param namespace - The namespace for the CustomXml part
 * @param data - The data to serialize and persist
 * @param dataSerializer - Optional function to transform data before serialization (default: identity)
 * @param logPrefix - Prefix for error logging
 */
export const persistDataToCustomXml = async <T>(
  namespace: string,
  data: T[],
  dataSerializer: (data: T[]) => any = (d) => d,
  logPrefix: string = "[CustomXmlCoHelper][persistDataToCustomXml]"
): Promise<void> => {
  try {
    const serializedData = dataSerializer(data);
    const json = JSON.stringify(serializedData);
    const xml = `<CustomXMLCo xmlns="${namespace}"><![CDATA[${json}]]></CustomXMLCo>`;

    // Use Office.context to retrieve existing parts
    const xmlParts = await new Promise<Office.CustomXmlPart[]>((resolve, reject) => {
      Office.context.document.customXmlParts.getByNamespaceAsync(namespace, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(new Error(`${logPrefix} Failed to get custom XML parts for namespace: ${namespace}`));
        }
      });
    });

    if (xmlParts.length === 0) {
      // No existing parts: add via Word.run
      await Word.run(async (context) => {
        context.document.customXmlParts.add(xml);
        await context.sync();
      });
      return;
    }

    // Delete extra parts if more than one, using Office.context deleteAsync
    if (xmlParts.length > 1) {
      const [, ...others] = xmlParts;
      await Promise.all(
        others.map(
          (part) =>
            new Promise<void>((resolve, reject) => {
              part.deleteAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolve();
                } else {
                  reject(new Error(`${logPrefix} Failed to delete extra XML part for namespace: ${namespace}`));
                }
              });
            })
        )
      );
    }

    const firstPartId = xmlParts[0].id;
    await Word.run(async (context) => {
      const part = context.document.customXmlParts.getItem(firstPartId);
      part.setXml(xml);
      await context.sync();
    });
  } catch (e) {
    console.error(`${logPrefix} Unexpected error:`, e);
    throw e;
  }
};
